import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Table from '../../components/ui/Table';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import Loading from '../../components/ui/Loading';
import Alert from '../../components/ui/Alert';
import { Plus, MapPin, Edit2, Trash2, Camera, Search, Eye, ExternalLink } from 'lucide-react';
import { locationService } from '../../services';
import type { Location } from '../../types';
import { nvrHttpUrl } from '../../utils/nvrAccess';

const LocationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [locations, setLocations] = useState<Location[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [deleteDependencies, setDeleteDependencies] = useState<{ cameras: number; events: number } | null>(null);
  const [showForceDeleteWarning, setShowForceDeleteWarning] = useState(false);

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await locationService.getLocations();
      setLocations(response.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch locations');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async (term: string) => {
    setSearchTerm(term);
    if (!term.trim()) {
      fetchLocations();
      return;
    }

    try {
      setIsLoading(true);
      const response = await locationService.searchLocations(term);
      setLocations(response.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (force: boolean = false) => {
    if (!selectedLocation) return;

    try {
      await locationService.deleteLocation(selectedLocation.ycl_id, force);
      setIsDeleteModalOpen(false);
      setSelectedLocation(null);
      setDeleteDependencies(null);
      setShowForceDeleteWarning(false);
      fetchLocations();
    } catch (err: any) {
      // Handle 409 Conflict - has dependencies
      if (err?.response?.status === 409) {
        const errorDetail = err.response.data?.detail || {};
        if (errorDetail.dependencies) {
          setDeleteDependencies(errorDetail.dependencies);
          setShowForceDeleteWarning(false);
          return; // Keep modal open to show dependencies
        }
      }
      // Other errors
      setError(err instanceof Error ? err.message : 'Failed to delete location');
      setIsDeleteModalOpen(false);
    }
  };

  const handleForceDelete = () => {
    setShowForceDeleteWarning(true);
  };

  const confirmForceDelete = async () => {
    await handleDelete(true);
  };

  const openDeleteModal = (location: Location) => {
    setSelectedLocation(location);
    setDeleteDependencies(null);
    setShowForceDeleteWarning(false);
    setIsDeleteModalOpen(true);
  };

  const columns = [
    {
      key: 'ycl_name',
      label: 'Location Name',
      render: (location: Location) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-accent-primary/10 rounded-lg flex items-center justify-center">
            <MapPin size={16} className="text-accent-primary" />
          </div>
          <div>
            <div className="font-medium text-text-primary">{location.ycl_name}</div>
            <div className="text-xs text-text-muted">{location.ycl_code}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'location',
      label: 'Location',
      render: (location: Location) => (
        <div>
          <div className="text-text-primary">{location.ycl_city || 'N/A'}</div>
          <div className="text-xs text-text-muted">{location.ycl_province || ''}</div>
        </div>
      ),
    },
    {
      key: 'cameras',
      label: 'Cameras',
      render: (location: Location) => (
        <div className="flex items-center gap-2">
          <Camera size={14} className="text-text-muted" />
          <span className="text-text-primary">
            {location.camera_details?.length || 0}
          </span>
          {location.camera_details && location.camera_details.length > 0 && (
            <Badge variant="success" size="sm">
              {location.camera_details.filter(c => c.status).length} online
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'coordinates',
      label: 'Coordinates',
      render: (location: Location) => (
        <div className="text-sm text-text-muted">
          {location.ycl_latitude && location.ycl_longitude
            ? `${location.ycl_latitude.toFixed(4)}, ${location.ycl_longitude.toFixed(4)}`
            : 'N/A'}
        </div>
      ),
    },
    {
      key: 'ddns',
      label: 'DDNS',
      render: (location: Location) => (
        <div>
          {location.external_access_enabled && location.external_hostname ? (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                try { window.open(nvrHttpUrl(location.external_hostname!, location.external_http_port, location.nvr_port), '_blank', 'noopener,noreferrer'); }
                catch (error) { setError(error instanceof Error ? error.message : 'Check the NVR hostname and HTTP/ISAPI port.'); }
              }}
              leftIcon={<ExternalLink size={14} />}
            >
              Open NVR
            </Button>
          ) : (
            <span className="text-xs text-text-muted">Not configured</span>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right' as const,
      render: (location: Location) => (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/locations/${location.ycl_id}/view`);
            }}
            leftIcon={<Eye size={14} />}
          >
            View
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/locations/${location.ycl_id}/edit`);
            }}
            leftIcon={<Edit2 size={14} />}
          >
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              openDeleteModal(location);
            }}
            leftIcon={<Trash2 size={14} />}
            className="text-status-danger hover:text-status-danger"
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="page-stack">
        <div className="page-header">
          <div>
            <h1 className="page-title">Locations</h1>
            <p className="page-description">Manage your camera locations</p>
          </div>
          <Button
            variant="primary"
            size="md"
            leftIcon={<Plus size={18} />}
            onClick={() => navigate('/locations/create')}
          >
            Add Location
          </Button>
        </div>

        {error && (
          <Alert
            variant="error"
            message={error}
            onClose={() => setError(null)}
          />
        )}

        <Card padding="md">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search locations by name, code, city..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                leftIcon={<Search size={18} />}
                fullWidth
              />
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-3 gap-3 lg:gap-4">
          <Card padding="md">
            <div className="text-text-muted text-sm mb-1">Total Locations</div>
            <div className="text-2xl font-bold text-text-primary">{locations.length}</div>
          </Card>
          <Card padding="md">
            <div className="text-text-muted text-sm mb-1">Total Cameras</div>
            <div className="text-2xl font-bold text-text-primary">
              {locations.reduce((sum, loc) => sum + (loc.camera_details?.length || 0), 0)}
            </div>
          </Card>
          <Card padding="md">
            <div className="text-text-muted text-sm mb-1">Online Cameras</div>
            <div className="text-2xl font-bold text-text-primary">
              {locations.reduce(
                (sum, loc) =>
                  sum + (loc.camera_details?.filter(c => c.status).length || 0),
                0
              )}
            </div>
          </Card>
        </div>

        <Card padding="none">
          {isLoading ? (
            <div className="p-12">
              <Loading size="lg" text="Loading locations..." />
            </div>
          ) : (
            <Table
              data={locations}
              columns={columns}
              isLoading={isLoading}
              emptyMessage="No locations found. Click 'Add Location' to create one."
              hoverable
            />
          )}
        </Card>
      </div>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedLocation(null);
          setDeleteDependencies(null);
          setShowForceDeleteWarning(false);
        }}
        title={showForceDeleteWarning ? "⚠️ Force Delete Warning" : "Delete Location"}
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <Button
              variant="ghost"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setSelectedLocation(null);
                setDeleteDependencies(null);
                setShowForceDeleteWarning(false);
              }}
            >
              Cancel
            </Button>
            {showForceDeleteWarning ? (
              <Button variant="danger" onClick={confirmForceDelete}>
                Yes, Force Delete Everything
              </Button>
            ) : deleteDependencies ? (
              <Button variant="danger" onClick={handleForceDelete}>
                Force Delete
              </Button>
            ) : (
              <Button variant="danger" onClick={() => handleDelete(false)}>
                Delete
              </Button>
            )}
          </div>
        }
      >
        {selectedLocation && (
          <div className="space-y-4">
            {showForceDeleteWarning ? (
              <div className="space-y-3">
                <Alert
                  variant="error"
                  message="⚠️ DANGER: You are about to permanently delete this location and ALL associated data!"
                />
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-red-200 font-semibold mb-2">This will permanently delete:</p>
                  <ul className="list-disc list-inside text-red-200 space-y-1">
                    <li><strong>{deleteDependencies?.cameras || 0}</strong> camera(s) and their configurations</li>
                    <li><strong>{deleteDependencies?.events || 0}</strong> event record(s)</li>
                    <li>Location <strong>{selectedLocation.ycl_name}</strong> and all settings</li>
                  </ul>
                </div>
                <p className="text-text-primary font-semibold">
                  This action is IRREVERSIBLE. Are you absolutely sure?
                </p>
              </div>
            ) : deleteDependencies ? (
              <div className="space-y-3">
                <Alert
                  variant="warning"
                  message="⚠️ Cannot delete location: It has dependencies"
                />
                <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <p className="text-yellow-200 font-semibold mb-2">This location has:</p>
                  <ul className="list-disc list-inside text-yellow-200 space-y-1">
                    <li><strong>{deleteDependencies.cameras}</strong> camera(s) configured</li>
                    <li><strong>{deleteDependencies.events}</strong> event record(s)</li>
                  </ul>
                </div>
                <div className="space-y-2">
                  <p className="text-text-primary">
                    <strong>Recommended:</strong> Delete or reassign the cameras and events first.
                  </p>
                  <p className="text-text-muted text-sm">
                    Or, use <strong>Force Delete</strong> to permanently delete the location and all associated data (cameras, events, configurations). This cannot be undone!
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-text-primary">
                Are you sure you want to delete <strong>{selectedLocation.ycl_name}</strong>? This action cannot be undone.
              </p>
            )}
          </div>
        )}
      </Modal>
    </MainLayout>
  );
};

export default LocationsPage;
